import { useId, useState, type ReactNode } from "react";

import { IconChevronDown } from "@/components/Icons";

import styles from "./kit.module.css";

/**
 * A part of a form that is not always needed.
 *
 * The sport editor's cards had grown three and four sub-lists each, every one
 * expanded, every one in its own bordered box — a card inside a card inside a
 * section. The information was all correct and none of it was findable, because
 * nothing said which parts most people never touch.
 *
 * So the essentials stay on the card and the rest lives behind one of these. What
 * makes it work is the count: a shut row that says "3" is not hiding anything, it
 * is summarising it, and a shut row that says nothing is genuinely empty.
 *
 * Open when it has content, shut when it does not. That default is the whole
 * trick — a kind of fixture with two entry fields shows them, and one with none
 * stays a single line rather than an empty box asking to be filled.
 */
export function Disclosure({
  title,
  note,
  count,
  children,
}: {
  title: string;
  /** What this part is for, in the operator's words. Shown only when open, so a
   *  shut form is a list of titles rather than a wall of prose. */
  note?: string;
  /** How many things are inside. Absent for a part that is not a list. */
  count?: number;
  children: ReactNode;
}) {
  const id = useId();
  const [open, setOpen] = useState((count ?? 0) > 0);

  return (
    <div className={open ? `${styles.disc} ${styles.discOpen}` : styles.disc}>
      <button
        type="button"
        className={styles.discHead}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((was) => !was)}
      >
        <span className={open ? styles.discChevron : `${styles.discChevron} ${styles.discShut}`}>
          <IconChevronDown size={14} />
        </span>
        <span className={styles.discTitle}>{title}</span>
        {count !== undefined && (
          <span className={count > 0 ? `${styles.discCount} ${styles.discSome}` : styles.discCount}>
            {count > 0 ? count : "none"}
          </span>
        )}
      </button>
      {open && (
        <div className={styles.discBody} id={id}>
          {note && <p className={styles.discNote}>{note}</p>}
          {children}
        </div>
      )}
    </div>
  );
}
